import {handleActions} from 'redux-actions';
import {Map, List} from 'immutable';

import * as InvoiceModalActions from 'web-app/react/modals-container/invoice/actions';
import * as AccountItemActions from 'web-app/react/entities/account-items/actions';
import * as RecurringPaymentActions from 'web-app/react/entities/recurring-payments/actions';
import * as CreditActions from 'web-app/react/entities/credits/actions';
import * as SurchargeActions from 'web-app/react/entities/surcharges/actions';
import BehaviorsCollection from 'web-app/behaviors/behaviors-collection';

import * as Actions from './actions';
import BillPayer, {BillPayerTag} from './model';

// We want to preserve any balance related fields behaviors
// we might bave on existing bill payers.
const updateExistingWithNew = (existing, newBillPayer) => {
    if (existing) {
        return newBillPayer.merge({
            balanceBehaviors: existing.balanceBehaviors,
            creditBehaviors: existing.creditBehaviors,
            surchargeBehaviors: existing.surchargeBehaviors,
            balance: existing.balance,
            recurringPaymentIds: existing.recurringPaymentIds,
            accountItemIds: existing.accountItemIds,
            expectedIds: existing.expectedIds,
            unexpectedIds: existing.unexpectedIds,
            fetchingRecurringPayments: existing.fetchingRecurringPayments,
            fetchingBalance: existing.fetchingBalance,
            tags: newBillPayer.tags ? newBillPayer.tags : existing.tags,
            paymentSources: newBillPayer.paymentSources ? newBillPayer.paymentSources : existing.paymentSources,
        });
    } else {
        return newBillPayer;
    }
};

export default handleActions(
    {
        [Actions.fetchAllSuccess.type]: (state, {payload}) => {
            return payload.billPayers.concat(payload.recipients || []).reduce((map, billPayer) => {
                return map.update(billPayer.billPayerId, BillPayer.default, existing =>
                    updateExistingWithNew(existing, billPayer),
                );
            }, state);
        },
        [Actions.fetchBillPayersSuccess.type]: (state, {payload}) => {
            return payload.billPayers.reduce((map, billPayer) => {
                return map.set(
                    billPayer.billPayerId,
                    billPayer.merge({
                        balance: payload.balances.get(billPayer.billPayerId).balance,
                    }),
                );
            }, state);
        },
        [Actions.createSuccess.type]: (state, {payload}) => state.set(payload.billPayer.billPayerId, payload.billPayer),
        [Actions.discontinueSuccess.type]: (state, {payload}) =>
            state.update(payload.billPayer.billPayerId, existing => updateExistingWithNew(existing, payload.billPayer)),
        [Actions.reviveSuccess.type]: (state, {payload}) =>
            state.update(payload.billPayer.billPayerId, existing => updateExistingWithNew(existing, payload.billPayer)),
        [Actions.fetchSuccess.type]: (state, {payload}) =>
            state.update(payload.billPayer.billPayerId, existing => updateExistingWithNew(existing, payload.billPayer)),
        [Actions.updateSuccess.type]: (state, {payload}) => {
            return state.update(payload.billPayer.billPayerId, existing =>
                updateExistingWithNew(existing, payload.billPayer),
            );
        },
        [Actions.updateNoteSuccess.type]: (state, {payload}) => {
            return payload.billPayers.reduce((map, billPayer) => {
                return map.update(billPayer.billPayerId, existing => updateExistingWithNew(existing, billPayer));
            }, state);
        },
        [RecurringPaymentActions.fetchBillPayerPayments.type]: (state, {payload}) => {
            return state.update(payload.billPayerId, BillPayer.default, billPayer =>
                billPayer.merge({
                    fetchingRecurringPayments: true,
                }),
            );
        },
        [RecurringPaymentActions.fetchBillPayerPaymentsSuccess.type]: (state, {payload}) => {
            const payments = payload.recurringPayments;
            return state.update(payload.billPayerId, BillPayer.default, billPayer =>
                billPayer.merge({
                    recurringPaymentIds: List(payments).map(item => item.recurringPaymentId),
                    fetchingRecurringPayments: false,
                }),
            );
        },
        [RecurringPaymentActions.createPaymentSuccess.type]: (state, action) => {
            const billPayerId = action.payload.billPayerId;
            const payment = action.payload.newPayment;
            return state.update(billPayerId, BillPayer.default, billPayer =>
                billPayer.merge({
                    recurringPaymentIds: billPayer.recurringPaymentIds.insert(0, payment.recurringPaymentId),
                }),
            );
        },
        [RecurringPaymentActions.deletePaymentSuccess.type]: (state, action) => {
            const billPayerId = action.payload.billPayerId;
            const recurringPaymentId = action.payload.oldRecurringPaymentId;
            return state.update(billPayerId, BillPayer.default, billPayer =>
                billPayer.merge({
                    recurringPaymentIds: billPayer.recurringPaymentIds.filter(id => id !== recurringPaymentId),
                }),
            );
        },
        [AccountItemActions.fetchBillPayerBalance.type]: (state, action) => {
            return state.update(action.payload.billPayerId, BillPayer.default, billPayer =>
                billPayer.merge({
                    fetchingBalance: true,
                }),
            );
        },
        [Actions.storeExpectedIds.type]: (state, action) => {
            return state.update(action.payload.billPayerId, BillPayer.default, billPayer =>
                billPayer.merge({
                    expectedIds: action.payload.expectedIds,
                }),
            );
        },
        [Actions.storeUnexpectedIds.type]: (state, action) => {
            return state.update(action.payload.billPayerId, BillPayer.default, billPayer =>
                billPayer.merge({
                    unexpectedIds: action.payload.unexpectedIds,
                }),
            );
        },
        [AccountItemActions.fetchBillPayerBalanceSuccess.type]: (state, action) => {
            const invoicing = action.payload.invoicing;
            return state.update(action.payload.billPayerId, BillPayer.default, billPayer =>
                billPayer.merge({
                    balance: invoicing.balance,
                    paymentDue: invoicing.paymentDue,
                    balanceBehaviors: new BehaviorsCollection(invoicing.behaviors),
                    accountItemIds: List(invoicing.items.map(item => item.id)),
                    fetchingBalance: false,
                }),
            );
        },
        [CreditActions.fetchSuccess.type]: (state, {payload}) => {
            return state.update(payload.billPayerId, BillPayer.default, billPayer =>
                billPayer.merge({
                    creditBehaviors: payload.behaviors,
                }),
            );
        },
        [SurchargeActions.fetchSuccess.type]: (state, {payload}) => {
            return state.update(payload.billPayerId, BillPayer.default, billPayer =>
                billPayer.merge({
                    surchargeBehaviors: payload.behaviors,
                }),
            );
        },
        [AccountItemActions.createPaymentSuccess.type]: (state, action) => {
            const billPayerId = action.payload.billPayerId;
            const payment = action.payload.newPayment;
            return state.update(billPayerId, BillPayer.default, billPayer =>
                billPayer.merge({
                    balance: billPayer.balance + payment.amount,
                    accountItemIds: billPayer.accountItemIds.insert(0, payment.id),
                }),
            );
        },
        [AccountItemActions.updatePaymentSuccess.type]: (state, action) => {
            const billPayerId = action.payload.billPayerId;
            const payment = action.payload.newPayment;
            const oldPayment = action.payload.oldPayment;
            return state.update(billPayerId, BillPayer.default, billPayer =>
                billPayer.merge({
                    balance: billPayer.balance - oldPayment.amount + payment.amount, // oldPayment is form data, payment is model data
                    accountItemIds: billPayer.accountItemIds.filter(id => id !== oldPayment.id).insert(0, payment.id),
                }),
            );
        },
        [AccountItemActions.deletePaymentSuccess.type]: (state, action) => {
            const billPayerId = action.payload.billPayerId;
            const oldPayment = action.payload.oldPayment;
            return state.update(billPayerId, BillPayer.default, billPayer =>
                billPayer.merge({
                    balance: billPayer.balance - oldPayment.amount, // oldPayment is form data
                    accountItemIds: billPayer.accountItemIds.filter(id => id !== oldPayment.id),
                }),
            );
        },
        [AccountItemActions.createInvoiceSuccess.type]: (state, action) => {
            const {billPayerId, invoices} = action.payload;
            const amount = invoices.reduce((reduced, invoice) => reduced + invoice.amount, 0);
            const ids = invoices.map(invoice => invoice.id);
            return state.update(billPayerId, BillPayer.default, billPayer =>
                billPayer.merge({
                    balance: billPayer.balance - amount,
                    accountItemIds: billPayer.accountItemIds.concat(ids),
                }),
            );
        },
        [AccountItemActions.createInvoiceWithGraphQLSuccess.type]: (state, action) => {
            const {billPayerId, invoices} = action.payload;
            const amount = invoices.reduce((reduced, invoice) => reduced + invoice.amount, 0);
            const ids = invoices.map(invoice => invoice.id);
            return state.update(billPayerId, BillPayer.default, billPayer =>
                billPayer.merge({
                    balance: billPayer.balance - amount,
                    accountItemIds: billPayer.accountItemIds.concat(ids),
                }),
            );
        },
        [InvoiceModalActions.cancelSuccess.type]: (state, {payload}) => {
            const {billPayerId, invoices} = payload;
            const amount = invoices.reduce((reduced, invoice) => reduced + invoice.amount, 0);
            const ids = invoices.map(invoice => invoice.id);
            return state.update(billPayerId, BillPayer.default, billPayer =>
                billPayer.merge({
                    balance: billPayer.balance - amount,
                    accountItemIds: billPayer.accountItemIds.concat(ids),
                }),
            );
        },
        [Actions.updateBillPayerCategories.type]: (state, {payload}) => {
            return state.update(payload.billPayerId, billPayer =>
                billPayer.merge({
                    tags: payload.tags.map(tag => new BillPayerTag(tag)),
                }),
            );
        },
    },
    new Map(),
);
